import { Fragment, forwardRef, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Menu, Transition } from "@headlessui/react";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { Icon } from "@circle-react/components/shared/Icon";
import { dropdowListInOut } from "@circle-react-uikit/animations";
import "./styles.scss";

export interface MenuItem {
  className?: string;
  hidden?: boolean;
  id?: string | number;
  label: ReactNode;
  value: any;
}

export interface SimpleDropdownProps {
  allowEmpty?: boolean;
  buttonTextClassName?: string;
  className?: string;
  dataTestId?: string;
  direction?: "up" | "down";
  disabled?: boolean;
  fullWidth?: boolean;
  menuItems?: MenuItem[];
  name?: string;
  onBlur?: () => void;
  onChange?: (value: any) => void;
  placeholder?: string;
  showAllItems?: boolean;
  truncateLabel?: boolean;
  value?: any;
}

export const SimpleDropdown = forwardRef<
  HTMLButtonElement,
  SimpleDropdownProps
>(
  (
    {
      value,
      fullWidth,
      showAllItems,
      menuItems,
      onChange,
      disabled,
      direction = "down",
      allowEmpty = false,
      placeholder,
      dataTestId,
      onBlur,
      name,
      className,
      buttonTextClassName,
      truncateLabel = false,
    },
    ref,
  ) => {
    const [buttonText, setButtonText] = useState<ReactNode>();

    useEffect(() => {
      if (!menuItems || menuItems?.length === 0) return;
      let selected;

      if (!value && !allowEmpty) {
        selected = menuItems[0];
      } else if (value) {
        selected = menuItems.find(option => option.value === value);
      }
      setButtonText(selected?.label || placeholder);
    }, [value, menuItems, allowEmpty, placeholder]);

    return (
      <Menu
        as="div"
        className={classNames("react-dropdown", {
          "react-dropdown--fullWidth": fullWidth,
          "react-dropdown--showAllItems": showAllItems,
        })}
        data-testid={dataTestId}
      >
        {({ open }) => (
          <>
            <Menu.Button
              ref={ref}
              disabled={disabled}
              className={classNames(
                "form-control react-dropdown__button disabled:react-dropdown__button--disabled",
                {
                  "react-dropdown__button--disabled": disabled,
                  "text-default ring-1 ring-current": open,
                },
                className,
              )}
              data-testid="dropdown-button"
              onBlur={onBlur}
              name={name}
            >
              <span
                className={classNames(
                  truncateLabel && "overflow-hidden truncate",
                  buttonTextClassName,
                )}
              >
                {buttonText}
              </span>
              <Icon
                type="cheveron-down"
                className="react-dropdown__button-icon shrink-0"
              />
            </Menu.Button>
            <Transition as={Fragment} show={open} {...dropdowListInOut}>
              <Menu.Items
                static
                className={classNames(
                  "react-dropdown__items",
                  direction === "up" ? "bottom-full !mb-1" : "!mt-1",
                )}
                data-testid="dropdown-items"
              >
                {menuItems?.map(item => {
                  if (item.hidden) return null;

                  return (
                    <Menu.Item key={item.id}>
                      {({ active }) => (
                        <button
                          type="button"
                          onClick={() => onChange?.(item.value)}
                          className={classNames(
                            "react-dropdown__item",
                            {
                              "react-dropdown__item--active": active,
                            },
                            item.className,
                          )}
                          data-testid="dropdown-option"
                        >
                          {item.label}
                        </button>
                      )}
                    </Menu.Item>
                  );
                })}
              </Menu.Items>
            </Transition>
          </>
        )}
      </Menu>
    );
  },
);

SimpleDropdown.displayName = "SimpleDropdown";
